import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useParams } from 'react-router-dom'
import { useUpdateWorkflowStep, useUpdateStepTaskResult } from '~/hooks/submittals'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '~/components/ui/Dialog'
import Button from '../ui/Button'
import { LoaderCircleIcon } from 'lucide-react'
import Label from '../ui/Label'
import DocumentDropZone, { type OnUploadFileParams } from '../Reviews/DocumentDropZone'

const productDataSchema = z.object({
  documentId: z.number().int()
})

type ProductDataDocument = z.infer<typeof productDataSchema>

interface UploadDocumentsProps {
  open: boolean
  setOpen: (open: boolean) => void
  onDocListCreated?: () => void
}

export default function UploaderProductDataDocumentDialog({
  open,
  setOpen,
  onDocListCreated
}: UploadDocumentsProps) {
  const { projectId, workflowInstanceId } = useParams<{
    projectId: string
    workflowInstanceId: string
  }>()

  const { mutateAsync: updateStepTaskResult, isPending: isUpdatingStepTaskResult } =
    useUpdateStepTaskResult(parseInt(projectId!, 10), workflowInstanceId!, 0, 0)

  const { mutate: updateWorkflowStep, isPending } = useUpdateWorkflowStep({
    onSuccess: async () => {
      await updateStepTaskResult({
        status: true
      })
      toast.success('Product Data uploaded successfully')
      onDocListCreated?.()
      setOpen(false)
      reset()
    },
    onError: (error) => {
      console.error('Error:', error)
      toast.success('Error uploading product data')
      onDocListCreated?.()
      setOpen(false)
      reset()
    }
  })

  const {
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ProductDataDocument>({
    resolver: zodResolver(productDataSchema)
  })

  const onModalOpenChange = (open: boolean) => {
    if (!open) {
      reset()
    }
    setOpen(false)
  }

  const onUploadFile = (uploadedFile: OnUploadFileParams) => {
    const { name, error, data } = uploadedFile

    const docType = name as Parameters<typeof setError>[0] &
      Parameters<typeof setValue>[0]

    clearErrors(docType)

    if (error) {
      setError(docType, {
        message: error.join('\n')
      })
      return
    }

    if (data) {
      setValue(docType, data.id)
    }
  }

  const onSubmit = (data: ProductDataDocument) => {
    if (!projectId || !workflowInstanceId) {
      console.error('Project ID and Workflow Instance ID are required to update the step')
      return
    }

    updateWorkflowStep({
      projectId: parseInt(projectId, 10),
      workflowInstanceId,
      stepIndex: 0,
      result: {
        result_type: 'upload',
        doc_id: data.documentId
      }
    })
  }

  const isLoading = isPending || isUpdatingStepTaskResult

  return (
    <>
      <Dialog onOpenChange={onModalOpenChange} open={open}>
        <DialogContent className="min-w-[660px]">
          <DialogHeader>
            <DialogTitle>Upload Product Data</DialogTitle>
            <DialogDescription className="sr-only">Create a new review</DialogDescription>
          </DialogHeader>
          <form
            id="submittal-review-form"
            onSubmit={(event) => void handleSubmit(onSubmit)(event)}
          >
            <div className="grid grid-cols-1 grid-rows-[auto_160px] gap-4">
              <div className="flex flex-col">
                <Label className="text-disabled font-normal">
                  Please upload the product data documents for each specified lighting
                  fixture.
                </Label>
              </div>
              <div>
                <DocumentDropZone
                  label="Drag and drop your product data files here, or"
                  buttonLabel="browse to upload"
                  onUploadFile={onUploadFile}
                  name="documentId"
                  icon
                />
                {errors.documentId && (
                  <p className="text-error text-xs">{errors.documentId?.message}</p>
                )}
              </div>
            </div>
          </form>
          <DialogFooter>
            <div className="flex gap-4">
              <Button
                variant="secondary"
                className="min-w-24"
                onClick={() => onModalOpenChange(false)}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                className="min-w-24"
                form="submittal-review-form"
                disabled={isLoading}
              >
                {isLoading && <LoaderCircleIcon className="mr-2 h-5 w-5 animate-spin" />}
                Continue
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
